import type { PropsWithChildren, ReactNode } from "react";

import { useTranslations } from "@ui/providers/useTranslations";

export type JSXInterpolation = string | number | ReactNode | JSX.Element | JSX.Element[];

export type I18nProps = (
  | { content: string }
  | {
      t: string;
    }
) & {
  defaultValue?: JSXInterpolation;
  options?: {
    removeMissedPlaceholders: boolean;
  };
};

export const I18n = ({ children = [], defaultValue, options, ...props }: PropsWithChildren<I18nProps>): JSX.Element => {
  const { dictionary } = useTranslations();

  const childNodes = children ?? [];
  const interpolations = childNodes instanceof Array ? childNodes : [childNodes];
  const usesDictionary = "t" in props;
  const content = usesDictionary ? dictionary[props.t] : props.content;

  const { removeMissedPlaceholders = true } = options || {};

  if (content === undefined && usesDictionary) {
    console.warn(`No translation found for key ${props.t}`);
    return <>{defaultValue || props.t}</>;
  }

  if (interpolations.length === 0) {
    return <>{content}</>;
  }

  // split content into parts AND placeholders
  // example: "Hello {0}! You have {1} new messages." => ["Hello ", "{0}", "! You have ", "{1}", " new messages."]
  const contentParts = content.split(/({\d+})/);

  const translatedParts = contentParts.map((part: string) => {
    // check if part is a placeholder
    const placeholderMatch = part.match(/{(\d+)}/);

    if (placeholderMatch === null) {
      return part;
    }

    const interpolation = interpolations[Number(placeholderMatch[1])];
    if (interpolation === undefined) {
      const translationKey = usesDictionary ? props.t : props.content;
      console.warn(
        `No interpolation found for placeholder ${placeholderMatch[0]} in translation key ${translationKey}`
      );
      return removeMissedPlaceholders ? "" : part;
    }

    return interpolation;
  });

  return <>{translatedParts}</>;
};
